/**
 * SignupForm - Multi-step signup form
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/utils/cn';
import { useAuthStore } from '@/stores';
import { UserTypeSelector } from './UserTypeSelector';
import type { SignUpData } from '@/types/auth';

interface SignupFormProps {
  onSuccess?: () => void;
}

type Step = 'type' | 'credentials' | 'profile' | 'verification';

export function SignupForm({ onSuccess: _onSuccess }: SignupFormProps) {
  const location = useLocation();
  const [step, setStep] = useState<Step>('type');
  const [formData, setFormData] = useState<Partial<SignUpData>>({
    user_type: 'individual',
    email: '',
    password: '',
    username: '',
    first_name: '',
    last_name: '',
    birth_date: '',
    occupation: '',
    company_name: '',
    contact_person_name: '',
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const { signUp, isLoading, error, clearError } = useAuthStore();

  const updateField = <K extends keyof SignUpData>(field: K, value: SignUpData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    clearError();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (step === 'type') {
      setStep('credentials');
      return;
    }

    if (step === 'credentials') {
      if (formData.password !== confirmPassword) {
        return;
      }
      setStep('profile');
      return;
    }

    if (step === 'profile') {
      const result = await signUp(formData as SignUpData);
      if (!result.error) {
        setStep('verification');
      }
    }
  };

  const goBack = () => {
    if (step === 'credentials') setStep('type');
    else if (step === 'profile') setStep('credentials');
  };

  const passwordStrength = getPasswordStrength(formData.password || '');

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Progress indicator */}
      <div className="flex justify-center gap-2 mb-6">
        {(['type', 'credentials', 'profile', 'verification'] as Step[]).map((s, i) => (
          <div
            key={s}
            className={cn(
              'w-2 h-2 rounded-full transition-all duration-300',
              step === s
                ? 'w-6 bg-hud-primary'
                : i < ['type', 'credentials', 'profile', 'verification'].indexOf(step)
                ? 'bg-hud-primary'
                : 'bg-glass-border'
            )}
          />
        ))}
      </div>

      {/* Error message */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm"
        >
          {error}
        </motion.div>
      )}

      <AnimatePresence mode="wait">
        {/* Step 1: User Type */}
        {step === 'type' && (
          <motion.div
            key="type"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-5"
          >
            <UserTypeSelector
              value={formData.user_type || 'individual'}
              onChange={(type) => updateField('user_type', type)}
            />

            <button
              type="submit"
              className={cn(
                'w-full py-3 rounded-xl font-semibold',
                'bg-gradient-to-r from-hud-primary to-teal-600',
                'text-white',
                'hover:shadow-lg hover:shadow-hud-primary/25',
                'transition-all duration-200'
              )}
            >
              Continue
            </button>
          </motion.div>
        )}

        {/* Step 2: Credentials */}
        {step === 'credentials' && (
          <motion.div
            key="credentials"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-4"
          >
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => updateField('email', e.target.value)}
                required
                className={cn(
                  'w-full px-4 py-3 rounded-xl',
                  'bg-slate-800/50 border border-glass-border',
                  'text-white placeholder-text-muted',
                  'focus:outline-none focus:border-hud-primary focus:ring-1 focus:ring-hud-primary',
                  'transition-all duration-200'
                )}
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">Username</label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => updateField('username', e.target.value)}
                required
                minLength={3}
                className={cn(
                  'w-full px-4 py-3 rounded-xl',
                  'bg-slate-800/50 border border-glass-border',
                  'text-white placeholder-text-muted',
                  'focus:outline-none focus:border-hud-primary focus:ring-1 focus:ring-hud-primary',
                  'transition-all duration-200'
                )}
                placeholder="Choose a username"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => updateField('password', e.target.value)}
                  required
                  minLength={8}
                  className={cn(
                    'w-full px-4 py-3 pr-12 rounded-xl',
                    'bg-slate-800/50 border border-glass-border',
                    'text-white placeholder-text-muted',
                    'focus:outline-none focus:border-hud-primary focus:ring-1 focus:ring-hud-primary',
                    'transition-all duration-200'
                  )}
                  placeholder="Create a password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-secondary"
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              {/* Password strength */}
              <div className="mt-2 flex gap-1">
                {[1, 2, 3, 4].map((level) => (
                  <div
                    key={level}
                    className={cn(
                      'h-1 flex-1 rounded-full transition-colors',
                      passwordStrength >= level
                        ? level <= 1 ? 'bg-red-500' : level <= 2 ? 'bg-yellow-500' : level <= 3 ? 'bg-blue-500' : 'bg-green-500'
                        : 'bg-glass-border'
                    )}
                  />
                ))}
              </div>
              <p className="text-xs text-text-muted mt-1">
                {passwordStrength === 0 && 'Enter a password'}
                {passwordStrength === 1 && 'Weak - add more characters'}
                {passwordStrength === 2 && 'Fair - add numbers or symbols'}
                {passwordStrength === 3 && 'Good - almost there'}
                {passwordStrength === 4 && 'Strong password'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">Confirm Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className={cn(
                  'w-full px-4 py-3 rounded-xl',
                  'bg-slate-800/50 border border-glass-border',
                  'text-white placeholder-text-muted',
                  'focus:outline-none focus:border-hud-primary focus:ring-1 focus:ring-hud-primary',
                  'transition-all duration-200',
                  confirmPassword && formData.password !== confirmPassword && 'border-red-500'
                )}
                placeholder="Confirm your password"
              />
              {confirmPassword && formData.password !== confirmPassword && (
                <p className="text-xs text-red-400 mt-1">Passwords do not match</p>
              )}
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={goBack}
                className={cn(
                  'flex-1 py-3 rounded-xl font-semibold',
                  'bg-glass-bg border border-glass-border',
                  'text-text-primary',
                  'hover:bg-slate-700',
                  'transition-all duration-200'
                )}
              >
                Back
              </button>
              <button
                type="submit"
                disabled={!formData.email || !formData.password || formData.password !== confirmPassword}
                className={cn(
                  'flex-1 py-3 rounded-xl font-semibold',
                  'bg-gradient-to-r from-hud-primary to-teal-600',
                  'text-white',
                  'hover:shadow-lg hover:shadow-hud-primary/25',
                  'transition-all duration-200',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                Continue
              </button>
            </div>
          </motion.div>
        )}

        {/* Step 3: Profile */}
        {step === 'profile' && (
          <motion.div
            key="profile"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-4"
          >
            {formData.user_type === 'individual' ? (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">First Name</label>
                    <input
                      type="text"
                      value={formData.first_name}
                      onChange={(e) => updateField('first_name', e.target.value)}
                      required
                      className={cn(
                        'w-full px-4 py-3 rounded-xl',
                        'bg-slate-800/50 border border-glass-border',
                        'text-white placeholder-text-muted',
                        'focus:outline-none focus:border-hud-primary focus:ring-1 focus:ring-hud-primary',
                        'transition-all duration-200'
                      )}
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">Last Name</label>
                    <input
                      type="text"
                      value={formData.last_name}
                      onChange={(e) => updateField('last_name', e.target.value)}
                      required
                      className={cn(
                        'w-full px-4 py-3 rounded-xl',
                        'bg-slate-800/50 border border-glass-border',
                        'text-white placeholder-text-muted',
                        'focus:outline-none focus:border-hud-primary focus:ring-1 focus:ring-hud-primary',
                        'transition-all duration-200'
                      )}
                      placeholder="Doe"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">Birth Date</label>
                  <input
                    type="date"
                    value={formData.birth_date}
                    onChange={(e) => updateField('birth_date', e.target.value)}
                    required
                    className={cn(
                      'w-full px-4 py-3 rounded-xl',
                      'bg-slate-800/50 border border-glass-border',
                      'text-white',
                      'focus:outline-none focus:border-hud-primary focus:ring-1 focus:ring-hud-primary',
                      'transition-all duration-200'
                    )}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">Occupation</label>
                  <input
                    type="text"
                    value={formData.occupation}
                    onChange={(e) => updateField('occupation', e.target.value)}
                    required
                    className={cn(
                      'w-full px-4 py-3 rounded-xl',
                      'bg-slate-800/50 border border-glass-border',
                      'text-white placeholder-text-muted',
                      'focus:outline-none focus:border-hud-primary focus:ring-1 focus:ring-hud-primary',
                      'transition-all duration-200'
                    )}
                    placeholder="e.g., Runner, Office Worker, Student"
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">Company Name</label>
                  <input
                    type="text"
                    value={formData.company_name}
                    onChange={(e) => updateField('company_name', e.target.value)}
                    required
                    className={cn(
                      'w-full px-4 py-3 rounded-xl',
                      'bg-slate-800/50 border border-glass-border',
                      'text-white placeholder-text-muted',
                      'focus:outline-none focus:border-hud-primary focus:ring-1 focus:ring-hud-primary',
                      'transition-all duration-200'
                    )}
                    placeholder="Your Company Name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">Contact Person Name</label>
                  <input
                    type="text"
                    value={formData.contact_person_name}
                    onChange={(e) => updateField('contact_person_name', e.target.value)}
                    required
                    className={cn(
                      'w-full px-4 py-3 rounded-xl',
                      'bg-slate-800/50 border border-glass-border',
                      'text-white placeholder-text-muted',
                      'focus:outline-none focus:border-hud-primary focus:ring-1 focus:ring-hud-primary',
                      'transition-all duration-200'
                    )}
                    placeholder="John Doe"
                  />
                </div>
              </>
            )}

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={goBack}
                className={cn(
                  'flex-1 py-3 rounded-xl font-semibold',
                  'bg-glass-bg border border-glass-border',
                  'text-text-primary',
                  'hover:bg-slate-700',
                  'transition-all duration-200'
                )}
              >
                Back
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className={cn(
                  'flex-1 py-3 rounded-xl font-semibold',
                  'bg-gradient-to-r from-hud-primary to-teal-600',
                  'text-white',
                  'hover:shadow-lg hover:shadow-hud-primary/25',
                  'transition-all duration-200',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Creating...
                  </span>
                ) : (
                  'Create Account'
                )}
              </button>
            </div>
          </motion.div>
        )}

        {/* Step 4: Verification */}
        {step === 'verification' && (
          <motion.div
            key="verification"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-4"
          >
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
              <svg className="w-8 h-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Check Your Email</h3>
            <p className="text-text-secondary text-sm mb-6">
              We've sent a verification link to<br />
              <span className="text-white font-medium">{formData.email}</span>
            </p>
            <Link
              to="/login"
              state={location.state}
              className={cn(
                'inline-block px-6 py-3 rounded-xl font-semibold',
                'bg-gradient-to-r from-hud-primary to-teal-600',
                'text-white',
                'hover:shadow-lg hover:shadow-hud-primary/25',
                'transition-all duration-200'
              )}
            >
              Go to Login
            </Link>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sign in link */}
      {step !== 'verification' && (
        <p className="text-center text-sm text-text-secondary pt-2">
          Already have an account?{' '}
          <Link to="/login" state={location.state} className="text-hud-primary hover:text-hud-primary/80 font-medium transition-colors">
            Sign in
          </Link>
        </p>
      )}
    </form>
  );
}

function getPasswordStrength(password: string): number {
  if (!password) return 0;

  let strength = 0;
  if (password.length >= 8) strength++;
  if (password.length >= 12) strength++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
  if (/\d/.test(password)) strength++;
  if (/[^a-zA-Z0-9]/.test(password)) strength++;

  return Math.min(4, strength);
}
